// Shared presentation only. Each page owns its query semantics and lifecycle.
export function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, char => ({"&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;"})[char]);
}
export function queryInputMarkup({ prefix = "fishymap", inputClass = "", placeholderKey = "map.search.placeholder", labelKey = "map.query.input_label", label = "Map query", statusId = "fishymap-query-status-detail", maxLength = 0 } = {}) {
  return `
    <div id="${prefix}-query-input-root" class="fishymap-query-input-root">
      <div id="${prefix}-query-surface" class="fishymap-query-surface relative pr-8" data-state="empty">
        <div id="${prefix}-query-editor" class="fishymap-query-editor">
          <div id="${prefix}-query-source-row" class="fishymap-query-source-row">
            <span
              id="${prefix}-query-source-measure"
              class="fishymap-query-source-measure"
              aria-hidden="true"
            ></span>
            <input
              id="${prefix}-search"
              class="fishymap-query-draft-input${inputClass ? ` ${inputClass}` : ""}"
              type="text"
              ${maxLength ? `maxlength="${maxLength}"` : ""}
              autocomplete="off"
              autocapitalize="off"
              enterkeyhint="search"
              spellcheck="false"
              placeholder=""
              data-i18n-attr-placeholder="${placeholderKey}"
              aria-label="${escapeHtml(label)}"
              data-i18n-attr-aria-label="${labelKey}"
              aria-describedby="${statusId}"
              aria-controls="${prefix}-query-suggestions"
              data-query-source-owner="native-input"
            />
          </div>
          <div id="${prefix}-query-selection-shell" class="fishymap-query-selection-shell fishymap-query-units not-prose" hidden>
            <div id="${prefix}-query-selection" class="fishymap-query-selection" hidden></div>
          </div>
        </div>
        <button
          id="${prefix}-query-clear"
          class="btn btn-ghost btn-xs btn-square absolute right-1 top-1/2 -translate-y-1/2"
          type="button"
          data-fishy-tooltip="Clear query"
          aria-label="Clear query"
          data-i18n-attr-data-fishy-tooltip="map.query.clear"
          data-i18n-attr-aria-label="map.query.clear"
          hidden
        >
          <svg class="fishy-icon size-4" viewBox="0 0 24 24" aria-hidden="true">
            <use width="100%" height="100%" href="/img/icons.svg#fishy-clear"></use>
          </svg>
        </button>
      </div>
      <div id="${prefix}-query-suggestions-shell" class="fishymap-query-suggestions-shell rounded-box border border-base-300 bg-base-100 shadow-xl overflow-hidden" hidden>
        <ul id="${prefix}-query-suggestions" class="list fishymap-query-suggestions-list w-full overflow-auto p-2"></ul>
      </div>
    </div>
`;
}
export function querySuggestionRowsMarkup(rows, highlightedIndex = -1) {
  return rows.map((row, index) => `
      <li class="list-row fishymap-query-suggestion-row p-0">
        <button type="button" class="card card-border fishymap-query-suggestion-option ${index === highlightedIndex ? "active" : ""}"
          data-query-suggestion-index="${index}" ${row.attributes || ""}>
          <span class="fishymap-query-suggestion-main min-w-0">
            <span class="fishymap-query-suggestion-primary">${row.primaryMarkup}</span>
          </span>
          <span class="fishymap-query-suggestion-side">
            ${row.kindLabel ? `<span class="badge badge-soft badge-xs fishymap-query-suggestion-kind">${escapeHtml(row.kindLabel)}</span>` : ""}
            <code class="fishymap-query-suggestion-code">${escapeHtml(row.insertText)}</code>
          </span>
        </button>
      </li>`).join("");
}

export function queryStatusMarkup(detailId) {
  return `<span class="fishymap-query-status-dot" aria-hidden="true"></span>
    <span class="fishymap-query-status-label" aria-hidden="true"></span>
    <span id="${escapeHtml(detailId)}" class="fishymap-query-status-detail sr-only"></span>`;
}
export function renderQueryStatus(status, surface, { state, relation, label, detail }) {
  status.dataset.state = state;
  status.querySelector('.fishymap-query-status-label').textContent = label;
  status.querySelector('.fishymap-query-status-detail').textContent = detail;
  status.dataset.fishyTooltip = detail;
  if (surface) Object.assign(surface.dataset, { state, phase: state, relation });
}
export function queryStatusIndicator(state) {
  return ({ applied: "applied", empty: "none", failed: "failed", invalid: "invalid", partial: "partial" })[state] || "updating";
}
export function queryMeaningCopy(t) {
  return Object.fromEntries(Object.entries({ appliedQuery:"applied_query", appliedTerm:"applied_term", exclude:"exclude", matchAll:"match_all", matchAny:"match_any", containerContaining:"container_containing", conjunctionAnd:"conjunction.and", conjunctionOr:"conjunction.or" }).map(([key,value])=>[key,t(`map.search.meaning.${value}`)]));
}
export const QUERY_FILTER_ICONS = Object.freeze({
  favourite: { icon: "heart-fill", iconClass: "text-error" },
  missing: { icon: "check-circle-dash-fill", iconClass: "text-warning" },
  caught: { icon: "ring-full", iconClass: "text-success" },
  "missing-titles": { icon: "medal-fill", iconClass: "text-warning" },
  title: { icon: "medal-fill", iconClass: "" },
  red: { icon: "nav-dex", iconClass: "text-item-grade-prize" },
  yellow: { icon: "nav-dex", iconClass: "text-item-grade-rare" },
  blue: { icon: "nav-dex", iconClass: "text-item-grade-high-quality" },
  green: { icon: "nav-dex", iconClass: "text-item-grade-general" },
  white: { icon: "nav-dex", iconClass: "text-item-grade-trash" },
  rod: { icon: "fishing-mode-rod", iconClass: "" },
  hotspot: { icon: "bird-fill", iconClass: "" },
  harpoon: { icon: "fishing-mode-harpoon", iconClass: "" },
  dried: { icon: "dry-fill", iconClass: "" },
  zone: { icon: "fullscreen-fill", iconClass: "" },
  "potential-zone": { icon: "question-mark", iconClass: "" },
});
export function queryIconMarkup(icon, iconClass = "") {
  return `<svg class="fishy-icon size-4 shrink-0 ${escapeHtml(iconClass)}" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="/img/icons.svg#fishy-${escapeHtml(icon)}"></use></svg>`;
}
